"use client";

import { ComponentProps, type ReactNode, useCallback } from "react";

import ChevronLeft from "@/assets/icons/arrow-left.svg?react";
import ChevronRight from "@/assets/icons/arrow-right.svg?react";
import { usePathname, useSearchParams } from "next/navigation";

import MoreHorizontal from "@/assets/icons/ellipses.svg?react";
import { cn } from "@/lib/tailwind";
import { CLink } from "@/components/common/customLink/CustomLink";

export interface PaginationWithLinksProps {
  // pageSizeSelectOptions?: {
  //   pageSizeSearchParam?: string;
  //   pageSizeOptions: number[];
  // };
  lang: string;
  totalCount: number;
  pageSize: number;
  page: number;
  pageSearchParam?: string;
  translation?: { next: string; previous: string };
}

/**
 * Navigate with Nextjs links (need to update your own `pagination.tsx` to use Nextjs Link)
 * 
 * @example
 * ```
 * <PaginationWithLinks
    page={1}
    pageSize={20}
    totalCount={500}
  />
 * ```
 */

type PaginationLinkProps = {
  isActive?: boolean;
} & ComponentProps<typeof CLink>;
const PaginationLinks = ({ className, isActive, ...props }: PaginationLinkProps) => (
  <CLink
    aria-current={isActive ? "page" : undefined}
    className={cn(
      "flex h-10 w-10 items-center justify-center text-sm font-semibold",
      isActive ? "bg-black text-white" : "bg-white text-black",
    )}
    {...props}
  />
);
export function PaginationWithLinks({
  lang,
  pageSize,
  totalCount,
  page,
  pageSearchParam,
  translation,
}: PaginationWithLinksProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const totalPageCount = Math.ceil(totalCount / pageSize);

  const buildLink = useCallback(
    (newPage: number) => {
      const key = pageSearchParam || "page";
      if (!searchParams) return `${pathname}?${key}=${newPage}`;
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.set(key, String(newPage));
      return `${pathname}?${newSearchParams.toString()}`;
    },
    [searchParams, pathname],
  );

  const renderPageNumbers = () => {
    const items: ReactNode[] = [];
    const maxVisiblePages = 5;

    if (totalPageCount <= maxVisiblePages) {
      for (let i = 1; i <= totalPageCount; i++) {
        items.push(
          <PaginationLinks key={i} isActive={page === i} href={buildLink(i)}>
            {i}
          </PaginationLinks>,
        );
      }
    } else {
      items.push(
        <PaginationLinks isActive={page === 1} key={1} href={buildLink(1)}>
          1
        </PaginationLinks>,
      );

      if (page > 3) {
        items.push(<MoreHorizontal key="ellipses-start" className="mt-1 w-4" />);
      }

      const start = Math.max(2, page === totalPageCount ? page - 2 : page - 1);
      const end = Math.min(totalPageCount - 1, page === 1 ? page + 2 : page + 1);

      for (let i = start; i <= end; i++) {
        items.push(
          <PaginationLinks key={i} href={buildLink(i)} isActive={page === i}>
            {i}
          </PaginationLinks>,
        );
      }

      if (page < totalPageCount - 2) {
        items.push(<MoreHorizontal key="ellipses-end" className="mt-1 w-4" />);
      }

      items.push(
        <PaginationLinks
          key={totalPageCount}
          href={buildLink(totalPageCount)}
          isActive={page === totalPageCount}
        >
          {totalPageCount}
        </PaginationLinks>,
      );
    }

    return items;
  };

  return (
    <div className="flex w-full items-center justify-center gap-3">
      {page != 1 && (
        <div className="flex cursor-pointer items-center gap-4">
          <CLink
            href={buildLink(Math.max(page - 1, 1))}
            aria-disabled={page === 1}
            className={cn(
              "flex items-center",
              page === 1 ? "pointer-events-none opacity-50" : undefined,
            )}
          >
            <div className="flex h-8 w-8 items-center justify-center">
              {lang === "en" ? <ChevronLeft /> : <ChevronRight />}
            </div>
            <span className="text-sm font-semibold">
              {translation?.previous ? translation.previous : "Previous"}
            </span>
          </CLink>
        </div>
      )}
      {renderPageNumbers()}
      {page != totalPageCount && (
        <div className="flex cursor-pointer items-center gap-4">
          <CLink
            aria-disabled={page === totalPageCount}
            href={buildLink(Math.min(page + 1, totalPageCount))}
            className={cn(
              "flex items-center",
              page === totalPageCount ? "pointer-events-none opacity-50" : undefined,
            )}
          >
            <span className="text-sm font-semibold">
              {translation?.next ? translation.next : "Next"}
            </span>
            <div className="flex h-8 w-8 items-center justify-center">
              {lang === "en" ? <ChevronRight /> : <ChevronLeft />}
            </div>
          </CLink>
        </div>
      )}
    </div>
  );
}
